import { IconButton, Tooltip } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { EmbedUILayout } from "./embedLayoutPreference";
import { EmbedLayoutSwapIcon } from "./EmbedLayoutIcon";

export interface EmbedLayoutToggleProps {
  layout: EmbedUILayout;
  onToggle: () => void;
  /** Top bar: show a short text label beside the icon. Sidebar: icon only. */
  variant?: "labeled" | "icon";
  size?: "sm" | "md";
  className?: string;
  /** Sidebar chrome uses sidebar-scoped ink on branded backgrounds. */
  tone?: "default" | "sidebar";
}

const SWITCH_LABEL = "Switch layout";
const SWITCH_TOOLTIP =
  "Switch between simple chat (new UI) and sidebar with menu (old UI)";

/** Toggle between compact chat (new UI) and classic sidebar (old UI). */
export function EmbedLayoutToggle({
  layout,
  onToggle,
  variant = "icon",
  size = "sm",
  className,
  tone = "default",
}: EmbedLayoutToggleProps) {
  const ink = tone === "sidebar" ? "text-sidebar-ink-secondary" : "text-ink-secondary";
  const isCompact = layout === "compact";
  const hint = isCompact ? "Sidebar view" : "Simple view";
  const iconSize = variant === "labeled" ? 28 : 24;
  // Sidebar header sits flush with the iframe top; "top" tooltips render behind
  // the host panel chrome (e.g. EOLAS #artie-panel-header). Match other sidebar
  // controls and open to the right instead.
  const tooltipPlacement = tone === "sidebar" ? "right" : "top";

  if (variant === "labeled") {
    return (
      <Tooltip title={SWITCH_TOOLTIP} placement={tooltipPlacement}>
        <button
          type="button"
          onClick={onToggle}
          aria-label={SWITCH_LABEL}
          className={cn(
            "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg",
            "px-2 py-1.5 text-[11px] font-semibold leading-none",
            ink,
            "transition-colors hover:bg-hover",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            className,
          )}
        >
          <EmbedLayoutSwapIcon size={iconSize} />
          <span className="whitespace-nowrap">{hint}</span>
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={SWITCH_TOOLTIP} placement={tooltipPlacement}>
      <IconButton
        size={size}
        label={SWITCH_LABEL}
        onClick={onToggle}
        className={cn(ink, className)}
      >
        <EmbedLayoutSwapIcon size={iconSize} />
      </IconButton>
    </Tooltip>
  );
}
